import { createAsyncThunk } from '@reduxjs/toolkit';
import QuoteSetResource from 'api/service/QuoteSetResource';
import QuotesResource from 'api/service/QuotesResource';
import QuoteSavedResource from 'api/service/QuoteSavedResource';
import CommonEnumsResource from 'api/service/CommonEnumsResource';
import { blobDownload } from 'utils/download';

// quote set
export const createQuoteSet = createAsyncThunk('marketplace/createQuoteSet', async (initialData) => {
  const quoteSetResource = new QuoteSetResource();
  const quotesResource = new QuotesResource();
  const {
    property: { id, name },
  } = initialData;
  const { status, data } = await quoteSetResource.create({ name }, id);
  if (status === 201) {
    await quotesResource.create({ name: 'New quote' }, data?.id);
    return data;
  }
  return {};
});

export const getQuoteSet = createAsyncThunk('marketplace/getQuoteSet', async (initialData) => {
  const quoteSetResource = new QuoteSetResource();
  const { id } = initialData;
  const { status, data } = await quoteSetResource.get(id);
  if (status === 200) {
    return data;
  }
  return {};
});

export const getQuoteSetProperty = createAsyncThunk('marketplace/getQuoteSetProperty', async (initialData) => {
  const quoteSetResource = new QuoteSetResource();
  const { id } = initialData;
  const { status, data } = await quoteSetResource.getProperty(id);
  if (status === 200) {
    return data;
  }
  return {};
});

// quote
export const listQuotes = createAsyncThunk('marketplace/listQuotes', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { id } = initialData;
  const { status, data } = await quotesResource.list(id, {
    size: 10,
    includes: 'items',
    orderBy: 'last_modified desc',
  });
  if (status === 200) {
    return data;
  }
  return [];
});

export const createQuote = createAsyncThunk('marketplace/createQuote', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { id, form } = initialData;
  const { status, data } = await quotesResource.create(form, id);
  if (status === 201) {
    return data;
  }
  return {};
});

export const updateQuote = createAsyncThunk('marketplace/updateQuote', async (initialData) => {
  const quotesResource = new QuotesResource();
  const quote = initialData;
  const { status } = await quotesResource.quote(quote?.id).update({ name: quote?.name });
  if (status === 200) {
    return quote;
  }
  return {};
});

export const deleteQuote = createAsyncThunk('marketplace/deleteQuote', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { quoteId } = initialData;
  const { status } = await quotesResource.quote(quoteId).delete();
  if (status === 202) {
    return quoteId;
  }
  return {};
});

export const generateQuotePdf = createAsyncThunk('marketplace/generateQuotePdf', async (initialData) => {
  const { quoteId, quoteName } = initialData;
  const quotesResource = new QuotesResource();
  const { data, status } = await quotesResource.quote(quoteId).download();
  if (status === 200) {
    // Trigger the download
    blobDownload(data, quoteName);
    return quoteId;
  }
});

export const updateQuoteStatus = createAsyncThunk('marketplace/updateQuoteStatus', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { quote, quoteStatus } = initialData;
  const { status, data } = await quotesResource.quote(quote?.id).updateStatus(quoteStatus);
  if (status === 200) {
    return data;
  }
  return {};
});

export const exportQuote = createAsyncThunk('marketplace/exportQuote', async (initialData, { rejectWithValue }) => {
  const quotesResource = new QuotesResource();
  const { quoteId, download } = initialData;
  const { status, data, headers } = await quotesResource.quote(quoteId).export(download);
  if (status === 200) {
    let finalFileName = 'purchase_order';
    const disposition = headers['content-disposition'];

    if (disposition) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);

      if (matches != null && matches[1]) {
        finalFileName = matches[1].replace(/['"]/g, '');
      }
    }

    if (download) {
      blobDownload(data, finalFileName);
    }
    return { success: true };
  }
  return rejectWithValue('Quote export failed.');
});

// purchase email
export const purchaseEmail = createAsyncThunk('marketplace/purchaseEmail', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { quoteId, supplierId, itemId, orgId, sendTogether } = initialData;
  const { status, data } = await quotesResource
    .quote(quoteId)
    .purchaseEmail()
    .get(supplierId, itemId, orgId, sendTogether);
  if (status === 200) {
    return data;
  }
});

export const sendQuotePurchaseOrder = createAsyncThunk(
  'marketplace/sendQuotePurchaseOrder',
  async (initialData, { rejectWithValue }) => {
    const quotesResource = new QuotesResource();
    const { emailData, quoteId } = initialData;
    const { status, data } = await quotesResource.quote(quoteId).purchaseEmail().send(emailData);
    if (status === 200) {
      return data;
    }
    return rejectWithValue('Unable to send Purchase Order Email');
  }
);

//purchase settings
export const getQuotePurchaseSettings = createAsyncThunk(
  'marketplace/getQuotePurchaseSettings',
  async (initialData) => {
    const quotesResource = new QuotesResource();
    const { quoteId } = initialData;
    const { status, data } = await quotesResource.quote(quoteId).purchaseSettings().get();
    if (status === 200) {
      return data;
    }
    return {};
  }
);

export const updateQuotePurchaseSettings = createAsyncThunk(
  'marketplace/updateQuotePurchaseSettings',
  async (initialData) => {
    const quotesResource = new QuotesResource();
    const { quoteId, formData } = initialData;
    const { status } = await quotesResource.quote(quoteId).purchaseSettings().update(formData);
    if (status === 200) {
      return {
        quoteId,
        orgId: formData?.selected_organisation_id,
      };
    }
    return {};
  }
);

// quote organizations
export const listQuoteOrganizations = createAsyncThunk('marketplace/listQuoteOrganizations', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { quoteId } = initialData;
  const { status, data } = await quotesResource.quote(quoteId).listOrganizations();
  if (status === 200) {
    return data;
  }
  return [];
});

// quote item
export const addQuoteProduct = createAsyncThunk('marketplace/addQuoteProduct', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { quoteId, variantId } = initialData;
  const { status, data } = await quotesResource.quote(quoteId).item().create(variantId);
  if (status === 201) {
    return data;
  }
  return {};
});

export const deleteQuoteProduct = createAsyncThunk(
  'marketplace/deleteQuoteProduct',
  async (initialData, { rejectWithValue }) => {
    const quotesResource = new QuotesResource();
    const { quoteId, itemId } = initialData;
    const { status } = await quotesResource.quote(quoteId).item().delete(itemId);
    if (status === 202) {
      return { quoteId, itemId };
    }
    return rejectWithValue('Unable to delete quote product');
  }
);

// saved quote
export const saveQuote = createAsyncThunk('marketplace/saveQuote', async (initialData) => {
  const quoteSavedResource = new QuoteSavedResource();
  const { quoteId, form } = initialData;
  const { status, data } = await quoteSavedResource.create({
    quote: quoteId,
    name: form?.name,
    includes: 'items,supplier,category',
  });
  if (status === 201) {
    return data;
  }
  return {};
});

export const listSavedQuotes = createAsyncThunk('marketplace/listSavedQuotes', async () => {
  const quoteSavedResource = new QuoteSavedResource();
  const { status, data } = await quoteSavedResource.list();
  if (status === 200) {
    return data;
  }
  return [];
});

export const loadQuote = createAsyncThunk('marketplace/loadQuote', async (initialData) => {
  const quoteSavedResource = new QuoteSavedResource();
  const { quoteSetId, quoteId } = initialData;
  const { status, data } = await quoteSavedResource.load(quoteId, {
    quote_set: quoteSetId,
    includes: 'items,supplier,category',
  });
  if (status === 201) {
    return data;
  }
  return {};
});

export const deleteSavedQuote = createAsyncThunk(
  'marketplace/deleteSavedQuote',
  async (initialData, { rejectWithValue }) => {
    const quoteSavedResource = new QuoteSavedResource();
    const { quoteId } = initialData;
    const { status } = await quoteSavedResource.delete(quoteId);
    if (status === 202) {
      return quoteId;
    }
    return rejectWithValue('Unable to delete saved quote');
  }
);

export const listQuoteConversations = createAsyncThunk('properties/listQuoteConversations', async (initialData) => {
  const quotesResource = new QuotesResource();
  const { quoteId } = initialData;
  const { status, data } = await quotesResource.quote(quoteId).conversations().list();
  if (status === 200) {
    return data;
  }
  return [];
});

//quote status enum
export const listQuoteStatus = createAsyncThunk('properties/listQuoteStatus', async () => {
  const commonEnumsResource = new CommonEnumsResource();
  const { status, data } = await commonEnumsResource.list('quotestatus');
  if (status === 200) {
    return data;
  }
  return [];
});
